<script setup>
// FLOW-13 (SPEC S1, R6, Q3; PLAN G2, G4, G14) — o diálogo "Novo segredo", um só para os três lugares
// que criam segredo: a aba Segredos (`SegredosDaConta`), a caixa do passo `http` e o "Criar segredo"
// do erro do compilador. Uma cópia por lugar seriam três regras do nome e três avisos da Q3.
//
// **O valor nunca reaparece.** Ele vai num campo de senha, segue ao `POST flow_secrets` e some:
// salvo ou fechado, o diálogo esquece o que foi digitado.
//
// **O nome novo (R6):** minúsculas, números e sublinhado, porque o nome serve ao `secret('nome')` do
// flow e ao `{{nome}}` da tool antiga, que só reconhece `[a-z0-9_]`. A tela confere antes de mandar,
// e o servidor confere de novo (`AccountSecret::NOME_NOVO`).
//
// **O aviso da Q3 (G4):** com o nome dentro da regra, o diálogo pergunta ao servidor, 400 ms depois
// da última tecla, quais agentes ainda guardam um valor próprio com esse nome, e diz quais antes de
// salvar. Só a resposta do último nome vale; a que falha não avisa nada, e salvar continua.
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import flowSecretsApi from 'dashboard/api/flowSecrets';
import { listaDeNomes } from './usoDoSegredo';

const props = defineProps({
  // Os nomes que o cofre já tem, para o "já existe" sem ir ao servidor.
  nomesExistentes: { type: Array, default: () => [] },
  // A conta, quando quem abre já a sabe (o editor de flow, pela rota); sem ela, a do painel.
  accountId: { type: Number, default: null },
});

const emit = defineEmits(['criado']);

// `AccountSecret::NOME_NOVO` no Rails (R6).
const NOME_NOVO = /^[a-z0-9_]+$/;
const ESPERA_DA_CONFERENCIA = 400;
// Numa linha só no template: quebrado, o `{{ }}` ganha espaço dentro do <code>.
const CLASSE_DO_CODIGO = 'px-1 font-mono text-sm rounded-md bg-n-alpha-black2';

const { t, locale } = useI18n();
const store = useStore();
const accountId = computed(
  () => props.accountId ?? store?.getters?.getCurrentAccountId
);

const dialogo = ref(null);
const nome = ref('');
const valor = ref('');
const salvando = ref(false);
const agentesComValor = ref([]);

const nomeForaDaRegra = computed(
  () => nome.value !== '' && !NOME_NOVO.test(nome.value)
);
const nomeRepetido = computed(() => props.nomesExistentes.includes(nome.value));
const podeSalvar = computed(
  () => NOME_NOVO.test(nome.value) && !nomeRepetido.value && valor.value !== ''
);
const agentesPorExtenso = computed(() =>
  listaDeNomes(agentesComValor.value, locale.value)
);

// -- G4: quem guarda o nome. Cada nome novo zera o aviso e agenda uma pergunta; a resposta de um nome
// que já mudou não pinta o aviso do nome de agora.
let temporizador = null;
let pergunta = 0;

function pararConferencia() {
  clearTimeout(temporizador);
  temporizador = null;
  pergunta += 1;
}

function conferir(nomeAgora) {
  pararConferencia();
  agentesComValor.value = [];
  if (!NOME_NOVO.test(nomeAgora) || nomeRepetido.value) return;
  const esta = pergunta;
  temporizador = setTimeout(async () => {
    try {
      const agentes = await flowSecretsApi.conferir(accountId.value, nomeAgora);
      if (esta === pergunta) agentesComValor.value = agentes;
    } catch (e) {
      // Sem a resposta, sem aviso: o aviso informa, não decide, e salvar continua de pé.
    }
  }, ESPERA_DA_CONFERENCIA);
}

watch(nome, conferir);

// O valor digitado não sobrevive ao diálogo: salvo ou fechado, ele some.
function esquecer() {
  valor.value = '';
  pararConferencia();
  agentesComValor.value = [];
}

// `nomeInicial`: o "Criar segredo" do erro do compilador (G16), que já sabe o nome.
function abrir(nomeInicial = '') {
  esquecer();
  nome.value = nomeInicial;
  if (nomeInicial) conferir(nomeInicial);
  dialogo.value?.open();
}

function fechar() {
  dialogo.value?.close();
}

// Erro conhecido do servidor vira frase; o resto, a frase da ação. Nunca o texto cru da resposta.
function avisarFalha(erro) {
  const semCifra = erro?.response?.data?.error === 'cofre_sem_cifra';
  useAlert(
    semCifra
      ? t('POWER_TOOLS.SECRETS.NO_CIPHER')
      : t('POWER_TOOLS.SECRETS.SAVE_ERROR')
  );
}

async function salvar() {
  if (!podeSalvar.value) return;
  salvando.value = true;
  try {
    const criado = await flowSecretsApi.create(accountId.value, {
      name: nome.value,
      secret: valor.value,
    });
    esquecer();
    fechar();
    useAlert(t('POWER_TOOLS.SECRETS.CREATED'));
    emit('criado', criado);
  } catch (erro) {
    avisarFalha(erro);
  } finally {
    salvando.value = false;
  }
}

onBeforeUnmount(pararConferencia);

defineExpose({ abrir, fechar });
</script>

<template>
  <Dialog
    ref="dialogo"
    :title="t('POWER_TOOLS.SECRETS.NEW_TITLE')"
    :description="t('POWER_TOOLS.SECRETS.NEW_DESCRIPTION')"
    :confirm-button-label="t('POWER_TOOLS.SECRETS.SAVE')"
    :disable-confirm-button="!podeSalvar"
    :is-loading="salvando"
    @confirm="salvar"
    @close="esquecer"
  >
    <div class="flex flex-col gap-4" data-novo-segredo-dialogo>
      <div class="flex flex-col gap-1.5" data-nome-novo>
        <Input
          v-model="nome"
          :label="t('POWER_TOOLS.SECRETS.NAME_LABEL')"
          :message-type="nomeForaDaRegra || nomeRepetido ? 'error' : 'info'"
          custom-input-class="font-mono leading-6 !pt-2 !pb-2"
          autocomplete="off"
          autofocus
          spellcheck="false"
        />
        <!-- A regra fica à vista o tempo todo. Fora dela, o glifo de erro vem antes e o texto continua
             neutro: status é glifo colorido, nunca frase inteira colorida. -->
        <p
          class="flex items-start gap-1.5 mb-0 text-label-small"
          :class="
            nomeForaDaRegra || nomeRepetido
              ? 'text-n-slate-12'
              : 'text-n-slate-11'
          "
          data-regra-do-nome
          :data-erro="nomeForaDaRegra || nomeRepetido ? '' : undefined"
        >
          <span
            v-if="nomeForaDaRegra || nomeRepetido"
            class="i-lucide-circle-x size-3.5 mt-px shrink-0 text-n-ruby-9"
            aria-hidden="true"
          />
          {{
            nomeRepetido
              ? t('POWER_TOOLS.SECRETS.NAME_TAKEN')
              : t('POWER_TOOLS.SECRETS.NAME_RULE')
          }}
        </p>
      </div>

      <div
        v-if="agentesComValor.length"
        class="flex items-start gap-2.5 px-3 py-2.5 border rounded-lg border-n-weak"
        role="status"
        data-aviso-da-q3
      >
        <span
          class="i-lucide-triangle-alert size-4 mt-0.5 shrink-0 text-n-amber-11"
          aria-hidden="true"
        />
        <I18nT
          keypath="POWER_TOOLS.SECRETS.NAME_KEPT_BY_AGENTS"
          :plural="agentesComValor.length"
          tag="p"
          class="mb-0 text-sm text-n-slate-12"
        >
          <template #agentes>{{ agentesPorExtenso }}</template>
          <template #nome>
            <code :class="CLASSE_DO_CODIGO">{{ nome }}</code>
          </template>
        </I18nT>
      </div>

      <div data-valor-do-segredo>
        <Input
          v-model="valor"
          type="password"
          :label="t('POWER_TOOLS.SECRETS.VALUE_LABEL')"
          :placeholder="t('POWER_TOOLS.SECRETS.VALUE_PLACEHOLDER')"
          :message="t('POWER_TOOLS.SECRETS.VALUE_HELP')"
          autocomplete="new-password"
          spellcheck="false"
        />
      </div>
    </div>
  </Dialog>
</template>
